import { motion } from "framer-motion";
import { ArrowDown, ShieldCheck, Headphones, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { JR_PHOTOS, JR_TRUST } from "./juniorData";

const rise = (d) => ({ initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] } });

const Sticker = ({ className, children, testId }) => (
  <span className={`absolute z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold shadow-xl ${className}`} data-testid={testId}>{children}</span>
);

const Collage = () => (
  <motion.div {...rise(0.3)} className="relative h-[380px] sm:h-[460px] lg:h-[520px]">
    <div className="absolute right-0 top-0 w-[72%] h-[70%] rounded-3xl overflow-hidden rotate-3 border-4 border-lime-300/80 shadow-2xl">
      <img src={JR_PHOTOS.group} alt="Teens learning to DJ together" className="w-full h-full object-cover" />
    </div>
    <div className="absolute left-0 bottom-0 w-[50%] h-[62%] rounded-3xl overflow-hidden -rotate-3 border-4 border-pink-400/80 shadow-2xl">
      <img src={JR_PHOTOS.coach} alt="Coach teaching a young DJ" className="w-full h-full object-cover object-top" />
    </div>
    <Sticker className="top-6 left-4 bg-lime-300 text-black -rotate-6" testId="jr-sticker-wwcc"><ShieldCheck className="w-3.5 h-3.5" /> WWCC + Police checked</Sticker>
    <Sticker className="bottom-[46%] right-2 bg-pink-500 text-white rotate-6"><Headphones className="w-3.5 h-3.5" /> CDJ-3000s + DJM-A9</Sticker>
    <Sticker className="bottom-4 right-6 bg-cyan-300 text-black -rotate-3"><Coffee className="w-3.5 h-3.5" /> Parent lounge</Sticker>
  </motion.div>
);

export const JuniorHero = () => (
  <section className="relative overflow-hidden pt-40 pb-16 md:pb-24" data-testid="jr-hero">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(190,242,100,0.32),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(236,72,153,0.38),transparent_55%),radial-gradient(ellipse_at_top_right,rgba(34,211,238,0.22),transparent_50%)]" />
    <div className="grain-overlay" />
    <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <motion.div {...rise(0)} className="flex flex-wrap gap-2 mb-6">
          <span className="px-3 py-1 rounded-full bg-lime-300 text-black text-xs font-bold uppercase tracking-wider">Ages 10–17</span>
          <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold uppercase tracking-wider">Summer School Holidays</span>
          <span className="px-3 py-1 rounded-full bg-pink-500 text-white text-xs font-bold uppercase tracking-wider" data-testid="jr-hours-chip">9AM–9PM · 7 days</span>
          <span className="px-3 py-1 rounded-full bg-cyan-300 text-black text-xs font-bold uppercase tracking-wider" data-testid="jr-dates-chip">From 19 Dec 2026</span>
        </motion.div>
        <motion.h1 {...rise(0.1)} className="font-display font-bold text-white tracking-[-0.03em] leading-[0.95] text-5xl sm:text-6xl lg:text-7xl" data-testid="jr-heading">
          Junior DJ
          <span className="block font-serif italic font-normal tracking-normal text-transparent bg-clip-text bg-gradient-to-r from-lime-300 via-yellow-200 to-pink-400 pr-2">Academy.</span>
        </motion.h1>
        <motion.p {...rise(0.2)} className="text-white/75 text-base md:text-lg mt-6 max-w-xl leading-relaxed">
          Keep kids busy, connected and creative this summer. One-on-one DJ coaching on club-standard Pioneer gear, in a safe, supportive studio where every student belongs.
        </motion.p>
        <motion.div {...rise(0.3)} className="flex flex-col sm:flex-row gap-3 mt-8">
          <a href="#jr-book">
            <Button className="bg-lime-300 hover:bg-lime-200 text-black rounded-full px-8 py-7 text-base font-bold w-full sm:w-auto shadow-[0_0_40px_rgba(190,242,100,0.35)]" data-testid="jr-hero-book-btn">
              Book a Lesson
            </Button>
          </a>
          <a href="#jr-packages">
            <Button variant="outline" className="border-white/25 text-white hover:bg-white/10 bg-transparent rounded-full px-8 py-7 text-base w-full sm:w-auto" data-testid="jr-hero-packages-btn">
              See Packages <ArrowDown className="w-4 h-4 ml-2" />
            </Button>
          </a>
          <a href="#jr-gift">
            <Button variant="outline" className="border-pink-400/60 text-pink-300 hover:bg-pink-500/10 bg-transparent rounded-full px-8 py-7 text-base w-full sm:w-auto" data-testid="jr-hero-gift-btn">
              Gift Vouchers
            </Button>
          </a>
        </motion.div>
        <motion.ul {...rise(0.4)} className="grid grid-cols-2 gap-x-4 gap-y-2 mt-10 max-w-md" data-testid="jr-trust-list">
          {JR_TRUST.map((t) => (
            <li key={t} className="flex items-center gap-2 text-white/80 text-sm">
              <ShieldCheck className="w-4 h-4 text-lime-300 flex-shrink-0" /> {t}
            </li>
          ))}
        </motion.ul>
      </div>
      <Collage />
    </div>
  </section>
);
